"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TrailingBuySellProcessor = void 0;
const TradeAPILib_1 = require("TradeAPILib");
const react_1 = require("react");
const trade_utils_lib_1 = require("trade_utils_lib");
const TextAndSliderSyncTool_1 = require("../CommonUtils/TextAndSliderSyncTool");
exports.TrailingBuySellProcessor = {
    useUserParams(side, triggerPrice, trailingPercentText, investSlider, invest, investAction, baseBalance, quoteBalance, symbol, orderType) {
        const smartTradeType = (0, react_1.useMemo)(() => {
            return side === TradeAPILib_1.ExchangeOrderSide.sell ? TradeAPILib_1.SmartTradeType.smart_trade_sell_only : TradeAPILib_1.SmartTradeType.smart_trade_buy_only;
        }, [side]);
        const trailingPercent = (0, react_1.useMemo)(() => {
            return new trade_utils_lib_1.SafeDecimal(trailingPercentText).div(100).toDecimalPlaces(3);
        }, [trailingPercentText]);
        const [precision, unit] = (0, react_1.useMemo)(() => {
            return [
                (side === TradeAPILib_1.ExchangeOrderSide.buy ? symbol.precisionCost : symbol.amount) || 0,
                side === TradeAPILib_1.ExchangeOrderSide.buy ? symbol.quote : symbol.baseDisplay
            ];
        }, [side, symbol]);
        const balance = (0, react_1.useMemo)(() => {
            if (side === TradeAPILib_1.ExchangeOrderSide.sell) {
                return trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(baseBalance, precision);
            }
            else {
                return trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(quoteBalance, precision);
            }
        }, [precision, baseBalance, quoteBalance, side]);
        const [minInvest, maxInvest] = (0, react_1.useMemo)(() => {
            var _a, _b, _c, _d;
            const b = new trade_utils_lib_1.SafeDecimal(balance);
            if (side === TradeAPILib_1.ExchangeOrderSide.sell) {
                const min = trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr((_a = symbol.limit) === null || _a === void 0 ? void 0 : _a.dumping.min, precision, trade_utils_lib_1.SafeDecimal.ROUND_CEIL);
                const d = trade_utils_lib_1.NumberCommonUtils.formatterDecimal((_b = symbol.limit) === null || _b === void 0 ? void 0 : _b.dumping.max, precision);
                const max = trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(trade_utils_lib_1.SafeDecimal.min(b, d), precision);
                return [min, max];
            }
            else {
                const min = trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr((_c = symbol.limit) === null || _c === void 0 ? void 0 : _c.spending.min, precision, trade_utils_lib_1.SafeDecimal.ROUND_CEIL);
                const d = trade_utils_lib_1.NumberCommonUtils.formatterDecimal((_d = symbol.limit) === null || _d === void 0 ? void 0 : _d.spending.max, precision);
                const max = trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(trade_utils_lib_1.SafeDecimal.min(b, d), precision);
                return [min, max];
            }
        }, [precision, symbol, balance]);
        const [parseInvest, parseInvestSlider] = TextAndSliderSyncTool_1.TextAndSliderSyncTool.useInvestAndInvestPercent(investAction, "", invest, investSlider, TradeAPILib_1.ExchangeOrderSide.sell, balance, precision);
        const volume = (0, react_1.useMemo)(() => {
            if (side === TradeAPILib_1.ExchangeOrderSide.sell) {
                if (Number(triggerPrice) && Number(parseInvest)) {
                    const v = new trade_utils_lib_1.SafeDecimal(triggerPrice).mul(new trade_utils_lib_1.SafeDecimal(parseInvest));
                    return trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(v, symbol.precision);
                }
            }
            else {
                if (Number(triggerPrice) && Number(parseInvest)) {
                    const v = new trade_utils_lib_1.SafeDecimal(parseInvest).div(new trade_utils_lib_1.SafeDecimal(triggerPrice));
                    return trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(v, symbol.amount);
                }
            }
            return "0";
        }, [triggerPrice, parseInvest, side, symbol]);
        const investState = (0, react_1.useMemo)(() => {
            const state = {
                state: "normal",
                min: minInvest,
                max: maxInvest
            };
            if (Number(parseInvest)) {
                if (new trade_utils_lib_1.SafeDecimal(parseInvest).lessThan(new trade_utils_lib_1.SafeDecimal(minInvest))) {
                    state.state = 'small';
                }
                if (new trade_utils_lib_1.SafeDecimal(parseInvest).greaterThan(new trade_utils_lib_1.SafeDecimal(maxInvest))) {
                    state.state = 'large';
                }
            }
            return state;
        }, [minInvest, balance, parseInvest, symbol.symbol]);
        const emptyErrors = (0, react_1.useMemo)(() => {
            const emptys = [];
            if (!Number(triggerPrice)) {
                emptys.push("order_trigger_price");
            }
            if (!Number(parseInvest)) {
                if (side === TradeAPILib_1.ExchangeOrderSide.buy) {
                    emptys.push("trade_purchase_volume");
                }
                else {
                    emptys.push("trade_sell_amount");
                }
            }
            if (!Number(trailingPercentText)) {
                emptys.push("trailing_stop_limit_order_trailing_percent");
            }
            return emptys;
        }, [triggerPrice, parseInvest, trailingPercentText]);
        return { triggerPrice, trailingPercentText, trailingPercent: trailingPercent.toString(),
            invest: parseInvest, investSlider: parseInvestSlider, investState, balance,
            side, symbol, smartTradeType, orderType, minInvest, volume, emptyErrors,
            investPrecision: precision, investUnit: unit
        };
    },
    getCreateOrderRequest: (params, $st) => {
        const { symbol, side, orderType, smartTradeType } = params;
        if (params.emptyErrors.length > 0) {
            throw $st("trade_params_not_fill_enough");
        }
        if (new trade_utils_lib_1.SafeDecimal(params.invest).greaterThan(new trade_utils_lib_1.SafeDecimal(params.balance))) {
            throw $st("not_enough_balance");
        }
        if (side === TradeAPILib_1.ExchangeOrderSide.sell) {
            if (params.investState.state === "small") {
                throw $st("trade_error_low_amount", {
                    amount: `${params.minInvest} ${symbol.baseDisplay}`
                });
            }
            else if (params.investState.state === "large") {
                throw $st("trade_error_high_amount", { amount: `${params.investState.max} ${symbol.baseDisplay}` });
            }
        }
        else {
            if (params.investState.state === "small") {
                throw $st("trade_error_low_cost", {
                    cost: `${params.minInvest} ${symbol.quote}`
                });
            }
            else if (params.investState.state === "large") {
                throw $st("trade_error_high_cost", { cost: `${params.investState.max} ${symbol.quote}` });
            }
        }
        const trailingPercent = new trade_utils_lib_1.SafeDecimal(params.trailingPercent);
        if (trailingPercent.lessThanOrEqualTo(0) || trailingPercent.greaterThanOrEqualTo(1)) {
            throw $st("trade_trailing_percent_range_error", { min: "0%", max: "100%" });
        }
        if (side === TradeAPILib_1.ExchangeOrderSide.buy) {
            return {
                base: symbol.base,
                quote: symbol.quote,
                type: orderType,
                smart_type: smartTradeType,
                buy: params.triggerPrice,
                trigger: "0",
                amount: params.invest,
                trailing_percentage: "0",
                loss_stop: "0",
                trailing_buy_percent: params.trailingPercent
            };
        }
        else {
            return {
                base: symbol.base,
                quote: symbol.quote,
                type: orderType,
                smart_type: smartTradeType,
                buy: "0",
                trigger: params.triggerPrice,
                amount: params.invest,
                trailing_percentage: params.trailingPercent,
                loss_stop: "0",
            };
        }
    }
};
